import { Icon } from '@scality/core-ui';
import { useClipboard } from '@scality/core-ui/dist/components/buttonv2/CopyButton.component';
import { Button } from '@scality/core-ui/dist/next';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { messagesKey } from '../../hooks/chat/conversationKeys';
import { useMcpBridge } from '../../mcp/McpBridgeProvider';
import type { ClientChatResponse } from '../../types/ClientMessage';
import { formatConversationAsMarkdown } from '../../utils/exportConversation';
import { GuardianContext } from '../GuardianProvider';
import { SidebarRowLabel, sidebarRowSelectedStyles, sidebarRowStyles } from './sidebarRow.styles';

type ChatHistoryItemProps = {
  id: string;
  title: string;
  variant?: 'italic';
};

const ChatHistoryItemBox = styled.div<{
  isSelected: boolean;
  variant?: 'italic';
}>`
  ${sidebarRowStyles}

  ${(props) => props.isSelected && sidebarRowSelectedStyles}
  cursor: ${(props) => (props.isSelected ? 'default' : 'pointer')};
  font-style: ${(props) => (props.variant === 'italic' ? 'italic' : 'normal')};
`;

const EXPORT_TOOLTIP = { overlay: 'Copy conversation as Markdown' };
const COPIED_TOOLTIP = { overlay: 'Copied!' };

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ id, title, variant }) => {
  const { conversationId, setConversationId, setIsSidebarOpen } = useContext(GuardianContext);
  const { isInIframe } = useMcpBridge();
  const queryClient = useQueryClient();
  const { copy, copyStatus } = useClipboard();

  const handleClick = useCallback(() => {
    setConversationId(id);
    if (isInIframe) setIsSidebarOpen(false);
  }, [id, setConversationId, isInIframe, setIsSidebarOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleExport = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const messages = queryClient.getQueryData<ClientChatResponse[]>(messagesKey('chat', id)) ?? [];
      copy(formatConversationAsMarkdown(title, messages));
    },
    [id, title, queryClient, copy],
  );

  return (
    <ChatHistoryItemBox
      isSelected={conversationId === id}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      variant={variant}
      role="listitem"
      tabIndex={0}
    >
      <SidebarRowLabel>{title}</SidebarRowLabel>
      <Button
        icon={<Icon name="Copy" />}
        onClick={handleExport}
        tooltip={copyStatus === 'success' ? COPIED_TOOLTIP : EXPORT_TOOLTIP}
        aria-label="Copy conversation as Markdown"
      />
    </ChatHistoryItemBox>
  );
};
